import React from "react";
import { ChangeEvent } from "react";
import { Link } from "react-router-dom";
import { RouteComponentPropsHistory } from "../../types";

import { apiGet } from "../../Api";
import { formatName, formatTelephone } from "../../Utilities";
import withTitle from "../withTitle";

import DelayInput from "../DelayInput";
import HasRight from "../HasRight";
import Indicator from "../Indicator";
import ViewToolbar from "../ViewToolbar";

import "../../blocks.css";
import "../../icomoon.css";
import "../../indicator.css";
import "../../list.css";
import "../../spinner.css";
import "../../table.css";
import "../../user.css";
import "../viewer.css";
import OverflowMenuItem from "../OverflowMenuItem";
import ToolbarMenuItem from "../ToolbarMenuItem";
import FileUploadDialog from "../FileUploadDialog";

interface IEmployeesViewState {
	isLoading: boolean;
	isBatchUploadDialogOpen: boolean;
	error?: string;
	employees: any[];
	filteredEmployees: any[];
	searchText: string;
}

class EmployeesView extends React.Component<
	RouteComponentPropsHistory<any, any>,
	IEmployeesViewState
> {
	constructor(props: Readonly<RouteComponentPropsHistory<any, any>>) {
		super(props);

		this.state = {
			employees: [],
			isLoading: false,
			isBatchUploadDialogOpen: false,

			filteredEmployees: [],
			searchText:
				(props.location.state && props.location.state.searchText) || "",
		};

		this.changeSearchChange = this.changeSearchChange.bind(this);
		this.toggleBatchUploadDialog = this.toggleBatchUploadDialog.bind(this);
		this.handleBatchSuccess = this.handleBatchSuccess.bind(this);
	}

	public async componentDidMount() {
		this.loadData();
	}

	public render() {
		return (
			<div className="viewer-root">
				<ViewToolbar>
					<HasRight right="employee:create">
						<Link
							to="/employees/add"
							className="icon-plus"
						/>
					</HasRight>
					<OverflowMenuItem>
						<ToolbarMenuItem right="employee:create">
							<button
								type="button"
								className="text-with-icon"
								onClick={this.toggleBatchUploadDialog}
								title="Create multiple employees and users at once">
								<i className="icon-upload" />
								<span>Batch upload...</span>
							</button>
						</ToolbarMenuItem>
					</OverflowMenuItem>
				</ViewToolbar>
				<FileUploadDialog
					isOpen={this.state.isBatchUploadDialogOpen}
					endpoint="employees/batch"
					accept=".csv,text/csv"
					closeOnSuccess={true}
					title="Batch upload"
					descriptionOverride="Batch list"
					onRequestClose={this.toggleBatchUploadDialog}
					onUploadSuccess={this.handleBatchSuccess}
				/>
				<div className="filter-control-box">
					<DelayInput
						type="search"
						className="fill-space"
						timeout={300}
						placeholder="Search..."
						value={this.state.searchText}
						onChangeDelayed={this.changeSearchChange}
					/>
				</div>

				<table className="table-vertical">
					<thead>
						<tr>
							<th className="col-smallest">Name & Email</th>
							<th className="col-smallest">Phone #</th>
							<th className="col-smallest">Location</th>
							<th>Groups</th>
							{/*<th>Tags</th>*/}
							<th className="col-smallest th-font-tiny col-center">
								Kiosk
								<br />
								Pin
							</th>
							<th className="col-smallest th-font-tiny col-center">
								Has
								<br />
								User
							</th>
							<th className="col-smallest" />
						</tr>
					</thead>
					<thead className="table-vertical-head-small">
						<tr>
							<th>Employees</th>
						</tr>
					</thead>
					<tbody>
						{this.state.isLoading ? (
							<tr>
								<td colSpan={10}>
									<div className="spinner-center lds-dual-ring" />
								</td>
							</tr>
						) : (
							this.state.filteredEmployees.map((a) => (
								<tr key={a.id}>
									<td>
										{formatName(a.name)}
										{a.email ? (
											<div>
												<small>{a.email}</small>
											</div>
										) : null}
									</td>
									<td>{a.phone ? formatTelephone(a.phone) : null}</td>
									<td>
										{a.location ? a.location.shortName || a.location.name : ""}
									</td>
									<td className="wrap">
										{a.groups
											? a.groups.map((g: any) => (
													<Indicator
														color="lightblue"
														key={g.id}
														text={g.name}
													/>
											  ))
											: null}
									</td>
									{/*<td className="wrap">
                                            {a.tags.map((t: any) => <Indicator color="lightblue" key={t} text={t} />)}
                                        </td>*/}
									<td
										className="col-center"
										data-caption="Kisok Pin: ">
										{a.hasKioskPin ? <i className="icon-check" /> : null}
									</td>
									<td
										className="col-center"
										data-caption="Has User: ">
										{a.userId ? <i className="icon-check" /> : null}
									</td>
									<td>
										<HasRight right="employee:write">
											<Link
												to={`/employees/${a.id}`}
												className="button small text-with-icon">
												<span>Edit</span>
											</Link>
										</HasRight>
									</td>
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>
		);
	}

	private async loadData() {
		try {
			this.setState({
				error: undefined,
				isLoading: true,
			});
			const employees = await apiGet<any[]>("employees?limit=1200");

			this.setState((s) => ({
				employees,
				filteredEmployees: this.filterEmployeeList(
					employees,
					s.searchText.toLowerCase()
				),
				isLoading: false,
			}));
		} catch (e) {
			this.setState({
				error: e.message,
				isLoading: false,
			});
		}
	}

	private changeSearchChange(e: ChangeEvent<HTMLInputElement>) {
		const searchValue = e.currentTarget.value;

		this.setState(
			(s) => {
				return {
					searchText: searchValue,

					filteredEmployees: this.filterEmployeeList(
						s.employees,
						searchValue.toLowerCase()
					),
				};
			},
			() => this.saveSearchState()
		);
	}

	private filterEmployeeList(list: any[], searchValue: string) {
		if (!searchValue) {
			return list;
		}

		return list.filter(
			(u) =>
				u.name.first.toLowerCase().includes(searchValue) ||
				u.name.last.toLowerCase().includes(searchValue) ||
				u.phone?.includes(searchValue) ||
				(u.location && u.location.name.toLowerCase().includes(searchValue)) ||
				!!u.tags.find((t: any) => t.toLowerCase().includes(searchValue)) ||
				!!u.groups.some((t: any) => t.name.toLowerCase().includes(searchValue))
		);
	}

	private saveSearchState() {
		const location = this.props.location;
		location.state = {
			searchText: this.state.searchText,
		};

		this.props.history.replace(location);
	}

	private toggleBatchUploadDialog() {
		this.setState((s) => ({
			isBatchUploadDialogOpen: !s.isBatchUploadDialogOpen,
		}));
	}

	private handleBatchSuccess() {
		this.loadData();
	}
}

export default withTitle("Employee Management", EmployeesView);
